import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filter === 'all') return true;
    return post.status === filter;
  });

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-zinc-400">Manage your blog posts</p>
          </div>
          <Link to="/admin/post/new" data-testid="create-post-btn">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
              <Plus className="mr-2 w-4 h-4" /> Create Post
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="glass border-zinc-800 p-6">
            <p className="text-zinc-400 text-sm mb-1">Total Posts</p>
            <p className="text-3xl text-white font-bold" data-testid="total-posts">{posts.length}</p>
          </Card>
          <Card className="glass border-zinc-800 p-6">
            <p className="text-zinc-400 text-sm mb-1">Published</p>
            <p className="text-3xl font-bold text-emerald-400" data-testid="published-posts">
              {posts.filter(p => p.status === 'published').length}
            </p>
          </Card>
          <Card className="glass border-zinc-800 p-6">
            <p className="text-zinc-400 text-sm mb-1">Drafts</p>
            <p className="text-3xl font-bold text-yellow-400" data-testid="draft-posts">
              {posts.filter(p => p.status === 'draft').length}
            </p>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-emerald-500 text-black' : 'border-zinc-700'}
            data-testid="filter-all"
          >
            All
          </Button>
          <Button
            variant={filter === 'published' ? 'default' : 'outline'}
            onClick={() => setFilter('published')}
            className={filter === 'published' ? 'bg-emerald-500 text-black' : 'border-zinc-700'}
            data-testid="filter-published"
          >
            Published
          </Button>
          <Button
            variant={filter === 'draft' ? 'default' : 'outline'}
            onClick={() => setFilter('draft')}
            className={filter === 'draft' ? 'bg-emerald-500 text-black' : 'border-zinc-700'}
            data-testid="filter-draft"
          >
            Drafts
          </Button>
        </div>

        {/* Posts List */}
        {filteredPosts.length === 0 ? (
          <Card className="glass border-zinc-800 p-12 text-center" data-testid="no-posts-message">
            <p className="text-zinc-400 text-white text-lg mb-4">No posts yet</p>
            <Link to="/admin/post/new">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
                Create Your First Post
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="glass border-zinc-800 p-6" data-testid={`post-${post.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl text-white font-semibold">{post.title}</h3>
                      <Badge
                        variant={post.status === 'published' ? 'default' : 'secondary'}
                        className={post.status === 'published' ? 'bg-emerald-500 text-black' : 'bg-yellow-500 text-black'}
                        data-testid={`post-status-${post.id}`}
                      >
                        {post.status}
                      </Badge>
                    </div>
                    {post.excerpt && (
                      <p className="text-zinc-400 text-sm mb-2 line-clamp-2">{post.excerpt}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                      <span>Created: {formatDate(post.created_at)}</span>
                      <span>•</span>
                      <span>Updated: {formatDate(post.updated_at)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {post.status === 'published' && (
                      <Link to={`/blog/${post.slug}`} data-testid={`view-post-${post.id}`}>
                        <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    )}
                    <Link to={`/admin/post/${post.id}`} data-testid={`edit-post-${post.id}`}>
                      <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-400"
                      onClick={() => handleDelete(post.id)}
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
